// External dependencies
import moment from 'moment';

// Internal dependencies
import { generateScore } from './generateScore';
import { validateSelections, filterInvalidActivities,getSchoolYear } from '../validation';
import { getScaledBenchmarkCutlinesOverTime } from '../benchmarkCutlinesOverTime';
import { shouldFilterStoryFromReport } from '../../../../services/EarlyReaderService';
import getArmScore from '../getArmScore';

export function generateProgressReport(activities, metricId, scaleId, benchmarkId, customBenchmarks, locale, selectedStatePercentileCuts) {
  // search for "ARM SPANISH SCORES" to find other places where ARM score is determined.

  // Check for valid selections and activities
  if(!activities || activities.length === 0 || !validateSelections(metricId, scaleId, benchmarkId)) {
    return {
      data: [],
      mostRecentScore: '',
    };
  }

  // Filter down on the valid activities
  let filteredActivities = filterInvalidActivities(activities,true,true,true);

  let data = filteredActivities.map(activity => {
    activity.scores.armScore = getArmScore(activity.student.id, activity, locale);
    let score = generateScore(activity, metricId, scaleId, true, locale);
    return {
      id: activity.activityId,
      timeRead: activity.sessionTime ? activity.sessionTime : '',
      storyTitle: activity.story && activity.story.title,
      storyGrade: activity.story && activity.story.grade,
      storyTags: activity.story && activity.story.tags,
      score,
      date: moment(activity.createdAt).valueOf(),
      type: activity.type,
      status: activity.status,
      displayStatus: activity.displayStatus,
      firstName: activity.student.first_name,
      hasComprehensionDialog: activity.comprehensionDialog && activity.comprehensionDialog.length
    };
  });

  // sort activites by date
  data = data.sort(function(a,b){
    let aDate = moment(a.date).isAfter(moment(b.date));
    if(aDate) return 1;
    return -1;
  });

  // Find the most recent score, ignoring early reader activities
  let latestScore, lastDate = null;
  for (const activity of data) {
    if (shouldFilterStoryFromReport(activity, scaleId)) continue;
    const date = new Date(activity.date)
    if (!lastDate || date > lastDate) {
      latestScore = activity.score;
      lastDate = date;
    }
  }

  let schoolYear = getSchoolYear();
  let startDate = moment(schoolYear.startDate).toString();
  let endDate = moment(schoolYear.endDate).toString();

  // Generate raw cutlines
  const cutlines = getScaledBenchmarkCutlinesOverTime(benchmarkId, scaleId, customBenchmarks, startDate, endDate, false, selectedStatePercentileCuts);
  let { lowCutline, midCutline, highCutline } = cutlines;

  // Determine the highest point on the graph
  const { yaxisMax } = determineHighPoints(data, highCutline);
  
  // Determine the lowest point on the graph
  const { yaxisMin } = determineLowPoints(data, lowCutline, scaleId);

  // Process the cutlines into a format that the progress report can consume
  const poorCutoff = lowCutline;
  const averageCutoff = midCutline.map((cutpoint, i) => [cutpoint[0], cutpoint[1] - lowCutline[i][1]]);
  const goodCutoff = highCutline.map((cutpoint, i) => [cutpoint[0], cutpoint[1] - midCutline[i][1]]);
  const greatCutoff = highCutline.map((cutpoint) => [cutpoint[0], yaxisMax - cutpoint[1]]);
 
  // handle legacy "On Track" cutline (25 to 75)
  const onTrackCutoff = highCutline.map((cutpoint, i) => [cutpoint[0], cutpoint[1] - lowCutline[i][1]]);

  return {
    data,
    latestScore,
    poorCutoff,
    averageCutoff,
    goodCutoff,
    greatCutoff,
    onTrackCutoff,
    yearStart: moment(startDate).valueOf(),
    yearEnd: moment(endDate).valueOf(),
    yMax: yaxisMax,
    yMin: yaxisMin,
    label: scaleId,
    scale: scaleId,
    benchmarkId,
    trendline: [], // trendline populated async after report loads
  };
}

function determineHighPoints(data, highCutline) {
  const highest = data.reduce( findHighScore, {score: -1000} );
  const highestScore = highest.score;
  const highestCutpoint = highCutline.reduce( findHighScoreInTuple, [null,-1000])[1];
  let yaxisMax = highestScore * 1.1;
  if(highestCutpoint > highestScore) {
    yaxisMax = highestCutpoint * 1.1;
  }
  return {highestScore, highestCutpoint, yaxisMax};
}

function determineLowPoints(data, lowCutline, scaleId) {
  const lowestScore = data.reduce( findLowScore, {score: 10000} ).score;
  const lowestCutpoint = lowCutline.reduce( findLowScoreInTuple, [null,10000])[1];
  let yaxisMin = lowestScore  * 1.1;
  if(lowestCutpoint < lowestScore) {
    yaxisMin = lowestCutpoint;
  }
  if(yaxisMin > 0) {
    if(scaleId == 'AREA') { // TODO use enum
      if(yaxisMin > 4) {
        yaxisMin = 4;
      } else {
        yaxisMin = yaxisMin - 1;
      }
    } else {
      yaxisMin = 0;
    }
  } else {
    yaxisMin = yaxisMin * 1.1;
  }
  return {lowestScore, lowestCutpoint, yaxisMin};
}

function findHighScore( max, cur ) {
  if(cur.score > max.score) {
    return cur;
  }
  return max;
}

function findLowScore( min, cur ) {
  if(cur.score < min.score) {
    return cur;
  }
  return min;
}

function findHighScoreInTuple( max, cur ) {
  if(cur[1] > max[1]) {
    return cur;
  }
  return max;
}

function findLowScoreInTuple( min, cur ) {
  if(cur[1] < min[1]) {
    return cur;
  }
  return min;
}